import { tt } from '@agos/shared'
import type { BadgeColor } from '@agos/ui'

/**
 * What the two registers have been saying.
 *
 * This is the screen somebody opens when a receipt did not print, so the useful
 * thing is not the list — it is being able to get to the one line that explains
 * it. Hence the search across the message *and* the context, which is where the
 * order number lives.
 *
 * Ported from legacy `pages/kasa-logs.tsx`.
 */

export interface KasaLog {
  id?: number
  level?: string
  source?: string
  message?: string
  /** Free-form JSON the register attached — order number, total, kasa id. */
  context?: Record<string, unknown> | null
  createdAt?: string
  /** Which physical till reported it. Legacy's type omits this; the API sends it. */
  deviceId?: string
}

export const LEVEL_BADGES: Record<string, { label: string; color: BadgeColor }> = {
  info: { label: 'Info', color: 'emerald' },
  warning: { label: tt('Varovanie'), color: 'orange' },
  error: { label: tt('Chyba'), color: 'red' },
}

export function levelBadge(level: string | undefined): { label: string; color: BadgeColor } {
  return LEVEL_BADGES[level ?? ''] ?? { label: level ?? '', color: 'zinc' }
}

/**
 * FB-065: legacy's filter labels lost their diacritics — *Kasa Restauracia*,
 * *Vsetky zavaznosti*, and the source label map spells *Reštaurácia* correctly two
 * lines below the option that spells it wrong. Repaired and routed through `tt()`.
 */
export const SOURCE_OPTIONS = [
  { value: 'all', label: tt('Obe kasy') },
  { value: 'kasa_restauracia', label: tt('Kasa Reštaurácia') },
  { value: 'kasa_rozvoz', label: tt('Kasa Rozvoz') },
]

export const LEVEL_OPTIONS = [
  { value: 'all', label: tt('Všetky závažnosti') },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: tt('Varovanie') },
  { value: 'error', label: tt('Chyba') },
]

/**
 * The register's own name for itself.
 *
 * §10A.1 replaced the two hardcoded registers with franchise-managed sales
 * channels, and §9.5 replaces this map with a channel lookup — after parity. Until
 * then a third register logs under its raw source string, which is at least
 * truthful.
 */
const SOURCE_LABELS: Record<string, string> = {
  kasa_rozvoz: tt('Rozvoz'),
  kasa_restauracia: tt('Reštaurácia'),
}

export function sourceLabel(source: string | undefined): string {
  return SOURCE_LABELS[source ?? ''] ?? source ?? ''
}

/**
 * Search across the message and the context.
 *
 * The context is where the order number is — `{"orderNumber":"10783", …}` — and
 * "which log line is about order 10783" is the question this screen exists to
 * answer. Legacy searches the message only, so typing an order number finds
 * nothing even though the line is right there.
 */
export function matchesSearch(log: KasaLog, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true

  if ((log.message ?? '').toLowerCase().includes(needle)) return true
  if (log.deviceId?.toLowerCase().includes(needle)) return true
  if (!log.context) return false

  return Object.entries(log.context).some(
    ([key, value]) =>
      key.toLowerCase().includes(needle) || stringifyValue(value).toLowerCase().includes(needle),
  )
}

export function filterLogs(
  logs: KasaLog[],
  { level, search }: { level: string; search: string },
): KasaLog[] {
  return logs.filter((log) => {
    if (level !== 'all' && log.level !== level) return false
    return matchesSearch(log, search)
  })
}

export interface LevelCounts {
  info: number
  warning: number
  error: number
}

/** What the header counts, so an error in a list of 200 is visible without scrolling. */
export function countLevels(logs: KasaLog[]): LevelCounts {
  return {
    info: logs.filter((log) => log.level === 'info').length,
    warning: logs.filter((log) => log.level === 'warning').length,
    error: logs.filter((log) => log.level === 'error').length,
  }
}

/**
 * A context value as text. Nested objects are serialised rather than becoming
 * `[object Object]` — a register's error context nests, and that is exactly the
 * value somebody is searching for.
 */
function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- objects handled above
  return String(value)
}

/** `{"total":10.15,"kasaId":"rozvoz"}` → `total: 10.15 · kasaId: rozvoz`. */
export function describeContext(context: Record<string, unknown> | null | undefined): string {
  if (!context) return ''
  return Object.entries(context)
    .map(([key, value]) => `${key}: ${stringifyValue(value)}`)
    .join(' · ')
}
